import {
  useCallback,
  useEffect,
  useInsertionEffect,
  useRef,
  useState,
} from 'react';
import { replaceEqualDeep } from '@tanstack/react-query';
import {
  useUserPreference,
  useUpsertUserPreference,
} from '@/api/rpc/hooks/core/useUserPreference';
import {
  SidebarPreferencesSchema,
  type QuickFindShortcut,
  type SidebarNavLayout,
  type SidebarPreferences,
} from '@erp/rpc';
import { readSidebarOpenFromCookie } from '@erp/ui/hooks/sidebar-cookie';
import { renameRouteIdsInPreferences } from '@/routing/route-id-renames';

const DEFAULTS: SidebarPreferences = {
  collapsedGroups: [],
  hiddenItems: [],
};

/** Last-known sidebar prefs, read synchronously so collapsed groups don't flash open. */
export const SIDEBAR_PREFERENCES_STORAGE_KEY = 'erp:sidebar-preferences';

export function readCachedSidebarPreferences(): SidebarPreferences | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SIDEBAR_PREFERENCES_STORAGE_KEY);
    if (!raw) return null;
    const parsed = SidebarPreferencesSchema.safeParse(JSON.parse(raw));
    return parsed.success ? renameRouteIdsInPreferences(parsed.data) : null;
  } catch {
    return null;
  }
}

export function writeCachedSidebarPreferences(prefs: SidebarPreferences): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      SIDEBAR_PREFERENCES_STORAGE_KEY,
      JSON.stringify(prefs),
    );
  } catch {
    // ignore quota / private-mode errors
  }
}

function parseSidebarPreferences(value: unknown): SidebarPreferences {
  const parsed = SidebarPreferencesSchema.safeParse(value);
  return parsed.success ? renameRouteIdsInPreferences(parsed.data) : DEFAULTS;
}

/**
 * Keeps object identity stable across renders when the content is deep-equal,
 * and preserves unchanged subtrees when it isn't. Zod re-parses the cached
 * preference row on every write, handing out fresh `layout`/`hiddenItems`
 * objects even when only `collapsedGroups` changed — which busted
 * useNavLayout's memo and re-rendered the entire nav tree in the middle of
 * the sidebar animations. Uses the render-phase state adjustment pattern so
 * it stays React Compiler compatible.
 */
function useDeepStable<T>(value: T): T {
  const [stable, setStable] = useState(value);
  const next = replaceEqualDeep(stable, value);
  if (next !== stable) {
    setStable(next);
  }
  return next;
}

type PrefsRef = { current: SidebarPreferences };

function persistSidebarPreferences(
  next: SidebarPreferences,
  mutate: (input: { key: 'sidebar'; value: SidebarPreferences }) => void,
  prefsRef: PrefsRef,
) {
  prefsRef.current = next;
  writeCachedSidebarPreferences(next);
  mutate({ key: 'sidebar', value: next });
}

function toggleCollapsedGroup(
  prefsRef: PrefsRef,
  persist: (next: SidebarPreferences) => void,
  group: string,
) {
  const current = prefsRef.current;
  const collapsed = current.collapsedGroups.includes(group)
    ? current.collapsedGroups.filter((g) => g !== group)
    : [...current.collapsedGroups, group];
  persist({ ...current, collapsedGroups: collapsed });
}

function toggleHiddenItem(
  prefsRef: PrefsRef,
  persist: (next: SidebarPreferences) => void,
  itemId: string,
) {
  const current = prefsRef.current;
  const hidden = current.hiddenItems.includes(itemId)
    ? current.hiddenItems.filter((i) => i !== itemId)
    : [...current.hiddenItems, itemId];
  persist({ ...current, hiddenItems: hidden });
}

/** Stable mutators — a fresh `setCollapsed` recreates `onOpenChange`. */
function useSidebarMutators(
  prefsRef: PrefsRef,
  persistRef: { current: (next: SidebarPreferences) => void },
) {
  const toggleGroup = useCallback(
    (group: string) => {
      toggleCollapsedGroup(prefsRef, persistRef.current, group);
    },
    [prefsRef, persistRef],
  );
  const toggleItem = useCallback(
    (itemId: string) => {
      toggleHiddenItem(prefsRef, persistRef.current, itemId);
    },
    [prefsRef, persistRef],
  );
  const setLayout = useCallback(
    (layout: SidebarNavLayout) => {
      persistRef.current({ ...prefsRef.current, layout });
    },
    [prefsRef, persistRef],
  );
  const clearLayout = useCallback(() => {
    const { layout: _, ...rest } = prefsRef.current;
    persistRef.current(rest);
  }, [prefsRef, persistRef]);
  const setCollapsed = useCallback(
    (collapsed: boolean) => {
      persistRef.current({ ...prefsRef.current, collapsed });
    },
    [prefsRef, persistRef],
  );
  // `undefined` clears the saved list so the built-in templates apply again.
  const setQuickFindShortcuts = (
    quickFindShortcuts: QuickFindShortcut[] | undefined,
  ) => {
    const { quickFindShortcuts: _, ...rest } = prefsRef.current;
    persistRef.current(
      quickFindShortcuts ? { ...rest, quickFindShortcuts } : rest,
    );
  };
  return {
    toggleGroup,
    toggleItem,
    setLayout,
    clearLayout,
    setCollapsed,
    setQuickFindShortcuts,
  };
}

export function useSidebarPreferences() {
  const { data, isSuccess } = useUserPreference('sidebar');
  const { mutate } = useUpsertUserPreference();
  const [cached] = useState(readCachedSidebarPreferences);

  const prefs = useDeepStable(
    data ? parseSidebarPreferences(data.value) : (cached ?? DEFAULTS),
  );
  const prefsRef = useRef(prefs);
  const persistRef = useRef<(next: SidebarPreferences) => void>(() => {});

  // Known only from a local cache or a successful server read — not from a
  // failed/idle query, which would otherwise persist DEFAULTS over a real row.
  const preferencesKnown = cached !== null || isSuccess;
  // Until prefs are known, collapse every group so remembered-closed sections
  // never paint open and then animate shut.
  const assumeAllCollapsed = !preferencesKnown;

  const persistIfKnown = useCallback(
    (next: SidebarPreferences) => {
      if (!preferencesKnown) return;
      persistSidebarPreferences(next, mutate, prefsRef);
    },
    [preferencesKnown, mutate],
  );

  // Insertion runs after this render and before any layout effect, so a child
  // useLayoutEffect on the same commit reads the current row. Persist also
  // writes the prefs ref immediately so stacked clicks in one tick compose.
  useInsertionEffect(() => {
    prefsRef.current = prefs;
    persistRef.current = persistIfKnown;
  });

  useEffect(() => {
    if (!isSuccess) return;
    writeCachedSidebarPreferences(
      data ? parseSidebarPreferences(data.value) : DEFAULTS,
    );
  }, [isSuccess, data]);

  const mutators = useSidebarMutators(prefsRef, persistRef);

  return {
    collapsedGroups: prefs.collapsedGroups,
    hiddenItems: prefs.hiddenItems,
    layout: prefs.layout,
    collapsed: prefs.collapsed,
    quickFindShortcuts: prefs.quickFindShortcuts,
    assumeAllCollapsed,
    preferencesKnown,
    ...mutators,
  };
}

/** First-paint open state: saved preference, else the sidebar cookie. */
export function readSidebarDefaultOpen(): boolean {
  const cached = readCachedSidebarPreferences();
  if (cached?.collapsed === true) return false;
  if (cached?.collapsed === false) return true;
  return readSidebarOpenFromCookie(true);
}

/**
 * Desktop sidebar open state. Follows the saved preference once it exists;
 * otherwise uses the cookie (or expanded) until the user toggles.
 */
export function usePersistedSidebarOpen(): {
  open: boolean;
  onOpenChange: (open: boolean) => void;
} {
  const { collapsed, setCollapsed, preferencesKnown } = useSidebarPreferences();
  const [fallbackOpen, setFallbackOpen] = useState(readSidebarDefaultOpen);
  const [pendingCollapsed, setPendingCollapsed] = useState<
    boolean | undefined
  >();

  const resolvedCollapsed = pendingCollapsed ?? collapsed;
  const open =
    typeof resolvedCollapsed === 'boolean' ? !resolvedCollapsed : fallbackOpen;

  const onOpenChange = useCallback(
    (next: boolean) => {
      setFallbackOpen(next);
      if (preferencesKnown) {
        setCollapsed(!next);
        return;
      }
      setPendingCollapsed(!next);
    },
    [preferencesKnown, setCollapsed],
  );

  useEffect(() => {
    if (!preferencesKnown || pendingCollapsed === undefined) return;
    setCollapsed(pendingCollapsed);
    setPendingCollapsed(undefined);
  }, [preferencesKnown, pendingCollapsed, setCollapsed]);

  return { open, onOpenChange };
}
